import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  FileText, Plane, Receipt, CheckCircle, CalendarClock,
  AlertCircle, RefreshCw,
} from "lucide-react";
import {
  useGetBookingDocuments,
  useGenerateVouchers,
  useGenerateInvoices,
  useGenerateBookingConfirmation,
  useGenerateSSRConfirmation,
  useGenerateInsuranceConfirmation,
  usePollPdfStatus,
  mapDocumentToUi,
  INVOICE_GENERATION_ENABLED,
} from "../../../api/document/useDocumentsApi";
import { useToast } from "../../../context/ToastContext";
import VouchersPanel from "./documents/VouchersPanel";
import InvoicesPanel from "./documents/InvoicesPanel";
import ConfirmationsPanel from "./documents/ConfirmationsPanel";
import PaymentsPanel from "./documents/PaymentsPanel";
import DispatchReviewModal from "../../Documents/DocumentCentre/components/DispatchReviewModal";
import { fetchDocumentPdf, errorMessage, saveBlob, openPendingWindow, showBlobIn } from "../../../api/document/documentCentreApi";
import { SkeletonList } from "./documents/shared";
import styles from "./BookingDetail.module.css";
import s from "./documents/documents.module.css";

/* Route every document into one of the spec families (§8), plus the payment
   requests and reminders Finance issues (§15). Primary key is document_type,
   with a subtype fallback so nothing is ever orphaned. */
const PAYMENT_SUBTYPES = ["payment_request", "payment_reminder"];
const classify = (d) => {
  if (PAYMENT_SUBTYPES.includes(d.documentSubtype)) return "payments";
  if (d.documentType === "INVOICE") return "invoices";
  if (d.documentType === "VOUCHER") return "vouchers";
  if (d.documentType === "BOOKING") return "confirmations";
  if (["sales_invoice", "credit_invoice", "debit_notes"].includes(d.documentSubtype)) return "invoices";
  if (["flight_voucher", "hotel_voucher", "transfer_voucher", "car_rental_voucher", "visa_voucher", "cruise_voucher"].includes(d.documentSubtype)) return "vouchers";
  return "confirmations";
};

// A booking edited more than this long AFTER a family was generated marks that
// family stale. The tolerance absorbs the sub-second gap of a save that happens
// in the same breath as a generation, so it never false-flags freshly built docs.
const STALE_TOLERANCE_MS = 1500;

const tsOf = (d) => {
  const t = new Date(d.updatedAt || d.createdAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

const DocumentsTab = ({ bookingRef, bookingUpdatedAt, customerEmail = "" }) => {
  const { data: documents, setData, loading, error, refetch, refetchSilent } = useGetBookingDocuments(bookingRef);
  const { execute: generateVouchers, loading: generatingVouchers } = useGenerateVouchers();
  const { execute: generateInvoices, loading: generatingInvoices } = useGenerateInvoices();
  const { execute: generateConfirmation, loading: generatingConfirmation } = useGenerateBookingConfirmation();
  const { execute: generateSSRConfirmation, loading: generatingSSR } = useGenerateSSRConfirmation();
  const { execute: generateInsuranceConfirmation, loading: generatingInsurance } = useGenerateInsuranceConfirmation();
  const [downloading, setDownloading] = useState(false);
  const { startPolling, stopPolling } = usePollPdfStatus(documents, setData);
  const { showToast } = useToast();

  const [activeSegment, setActiveSegment] = useState("vouchers");
  // The document the Send button was pressed on. It opens the Documents centre
  // review window, which applies the same release checks as everywhere else.
  const [sendFor, setSendFor] = useState(null);
  const [newDocIds, setNewDocIds] = useState(new Set());
  // Re-render heartbeat so the "last generated N minutes ago" labels stay honest
  // across a long-lived session (formatRelative reads the clock at render time).
  const [, setClockTick] = useState(0);

  // Always-fresh snapshot of the current documents so async handlers can diff
  // new-vs-regenerated ids without going stale in a closure.
  const documentsRef = useRef(documents);
  documentsRef.current = documents;

  // Holds the pending "clear new-doc animation" timer so overlapping generations
  // (e.g. vouchers then invoices within 2s) never stomp each other's window, and
  // it's cleared on unmount.
  const newDocTimerRef = useRef(null);
  useEffect(() => () => { if (newDocTimerRef.current) clearTimeout(newDocTimerRef.current); }, []);

  const groups = useMemo(() => {
    const g = { vouchers: [], invoices: [], confirmations: [], payments: [] };
    for (const d of documents) g[classify(d)].push(d);
    return g;
  }, [documents]);

  // Per-family freshness: newest generation time + whether the booking changed
  // since. Drives the "last generated" hint and the amber "regenerate" banner.
  const freshness = useMemo(() => {
    const meta = (docs) => {
      let last = 0;
      for (const d of docs) { const t = tsOf(d); if (t > last) last = t; }
      let isStale = false;
      if (last && bookingUpdatedAt) {
        const bt = new Date(bookingUpdatedAt).getTime();
        if (Number.isFinite(bt) && bt - last > STALE_TOLERANCE_MS) isStale = true;
      }
      return { lastGeneratedAt: last ? new Date(last).toISOString() : null, isStale };
    };
    return {
      vouchers: meta(groups.vouchers),
      invoices: meta(groups.invoices),
      confirmations: meta(groups.confirmations),
    };
  }, [groups, bookingUpdatedAt]);

  // Tick once a minute while any family has been generated, so relative-time
  // labels advance without waiting on an unrelated re-render. (isStale is a pure
  // timestamp comparison and doesn't need this — only the "ago" label does.)
  const hasGenerated = !!(
    freshness.vouchers.lastGeneratedAt ||
    freshness.invoices.lastGeneratedAt ||
    freshness.confirmations.lastGeneratedAt
  );
  useEffect(() => {
    if (!hasGenerated) return undefined;
    const id = setInterval(() => setClockTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [hasGenerated]);

  // One-shot: when documents first arrive, open the family that has the most
  // (vouchers wins ties). Guarded so it never overrides a manual tab switch.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || documents.length === 0) return;
    autoPicked.current = true;
    const order = ["vouchers", "invoices", "confirmations"];
    const best = order.reduce((a, b) => (groups[b].length > groups[a].length ? b : a), "vouchers");
    if (groups[best].length > 0) setActiveSegment(best);
  }, [documents, groups]);

  // Poll for background PDF uploads while any document still lacks a stored URL.
  // Keyed on the SET of pending ids (not the documents reference) so a no-progress
  // poll round can't restart the poller and reset its attempt budget.
  const pendingPdfKey = useMemo(
    () => documents.filter((d) => !d.pdfUrl && !d.pdfStuck).map((d) => d.id).join(","),
    [documents]
  );
  useEffect(() => {
    if (pendingPdfKey) startPolling();
    else stopPolling();
    return stopPolling;
  }, [pendingPdfKey, startPolling, stopPolling]);

  // Merge freshly generated/regenerated docs into state: existing rows are updated
  // in place (regeneration keeps the same id + immutable number), genuinely new
  // rows are prepended. Returns the set of truly-new ids so only those animate in.
  const mergeCreated = useCallback((createdRaw, before) => {
    const mapped = (createdRaw || []).map(mapDocumentToUi);
    if (!mapped.length) return new Set();
    setData((prev) => {
      const incoming = new Map(mapped.map((d) => [d.id, d]));
      const existingIds = new Set(prev.map((d) => d.id));
      const updated = prev.map((d) => (incoming.has(d.id) ? { ...d, ...incoming.get(d.id) } : d));
      const fresh = mapped.filter((d) => !existingIds.has(d.id));
      return [...fresh, ...updated];
    });
    return new Set(mapped.map((d) => d.id).filter((id) => !before.has(id)));
  }, [setData]);

  // Shared generate/regenerate flow. `force` is implicit: both endpoints rebuild
  // in place, so the SAME action covers first-generation and regeneration —
  // the button label is the only thing that changes with document count.
  const runGeneration = useCallback(async ({ api, noun, segment }) => {
    setActiveSegment(segment);
    const before = new Set(documentsRef.current.map((d) => d.id));
    const result = await api();
    if (!result.success) {
      showToast(result.message || `Failed to generate ${noun.toLowerCase()}`, "error");
      return;
    }
    const created = result.data?.created || [];
    const skipped = result.data?.skipped || [];
    const failed = result.data?.failed || [];
    const pdfRegenerating = result.data?.pdfRegenerating || 0;

    // Instant feedback: surface returned docs immediately (fresh data for existing,
    // prepend for new) and flag the genuinely-new ones for the slide-in animation.
    const newIds = mergeCreated(created, before);
    if (newIds.size > 0) {
      setNewDocIds(newIds);
      if (newDocTimerRef.current) clearTimeout(newDocTimerRef.current);
      newDocTimerRef.current = setTimeout(() => setNewDocIds(new Set()), 2000);
    }

    // Reconcile against canonical server state with no skeleton flash: this is the
    // ONLY path that surfaces voucher regenerations (returned as in-place updates
    // in `skipped`, not in `created`), orphan deletions, and the authoritative
    // pdf_url / issue_date. If it fails, fall back to an authoritative refetch so
    // we never leave stale rows sitting under a green success toast.
    let reconciled = await refetchSilent();
    if (!reconciled) reconciled = await refetch();

    // A partial/total failure is reported inside a 2xx response (anything that DID
    // generate is still saved). "nothing to do" (e.g. a booking with no eligible
    // products — the voucher endpoint reports this as a 2xx) is neutral, not green.
    const nothingToDo =
      created.length === 0 && skipped.length === 0 && failed.length === 0 && pdfRegenerating === 0;
    const failDetail = failed.length
      ? failed.map((f) => `${f.kind || f.type || "item"}: ${f.reason || "failed"}`).join("; ")
      : "";

    if (failed.length > 0) {
      // The orchestrator's top-level message is count-only on a partial success, so
      // append which family failed; the all-failed message already lists reasons.
      const base = result.message || `Some ${noun.toLowerCase()} could not be generated`;
      showToast(created.length > 0 && failDetail ? `${base} — ${failDetail}` : base, "error");
    } else if (nothingToDo) {
      showToast(result.message || `Nothing to generate for ${noun.toLowerCase()}`, "info");
    } else if (!reconciled) {
      showToast(`${noun} generated — reload to see the latest`, "error");
    } else {
      showToast(result.message || `${noun} ready`, "success");
    }

    // Pick up any PDFs still uploading in the background.
    startPolling();
  }, [mergeCreated, refetchSilent, refetch, showToast, startPolling]);

  const handleGenerateVouchers = useCallback(
    () => runGeneration({ api: () => generateVouchers(bookingRef), noun: "Vouchers", segment: "vouchers" }),
    [runGeneration, generateVouchers, bookingRef]
  );

  const handleGenerateInvoices = useCallback(
    () => runGeneration({ api: () => generateInvoices(bookingRef), noun: "Invoices", segment: "invoices" }),
    [runGeneration, generateInvoices, bookingRef]
  );

  // One action covers the whole BOOKING family, the way Vouchers covers all three
  // voucher types: the booking confirmation (§8.3.1) always, plus the services &
  // preferences (§8.3.2) and insurance (§8.3.3) confirmations when the booking
  // actually has those. A booking with no SSRs or no insurance makes those
  // endpoints answer "nothing to confirm" — that is a skip, not a failure, so it
  // must never turn the toast red.
  const handleGenerateConfirmation = useCallback(
    () => runGeneration({
      api: async () => {
        const [main, ...optional] = await Promise.all([
          generateConfirmation(bookingRef),
          generateSSRConfirmation(bookingRef),
          generateInsuranceConfirmation(bookingRef),
        ]);
        if (!main.success) return main;
        const fallbackType = ["ssr_confirmation", "insurance_confirmation"];
        const created = [
          ...(main.data?.created || []),
          ...optional.flatMap((r) => (r.success ? r.data?.created || [] : [])),
        ];
        const skipped = [
          ...(main.data?.skipped || []),
          ...optional.flatMap((r, i) => (r.success
            ? r.data?.skipped || []
            : [{ type: fallbackType[i], reason: r.message || "Nothing to confirm" }])),
        ];
        return { success: true, data: { created, skipped }, message: main.message };
      },
      noun: "Booking documents",
      segment: "confirmations",
    }),
    [runGeneration, generateConfirmation, generateSSRConfirmation, generateInsuranceConfirmation, bookingRef]
  );

  /* Download is customer-ready: the server applies the release checks and
     refuses a document that is not released, saying why (Documents spec §10).
     View opens the document for review; one that is not released carries an
     internal review mark. */
  const handleDownload = useCallback(async (doc) => {
    setDownloading(true);
    try {
      const blob = await fetchDocumentPdf(doc.id, "customer");
      const name = String(doc.documentNumber || "document").replace(/[^A-Za-z0-9._-]/g, "-");
      saveBlob(blob, `${name}.pdf`);
      showToast(`${doc.documentNumber || "Document"} downloaded`, "success");
    } catch (err) {
      showToast(await errorMessage(err, "Failed to download PDF"), "error");
    } finally {
      setDownloading(false);
    }
  }, [showToast]);

  const handleView = useCallback(async (doc) => {
    const win = openPendingWindow();
    try {
      showBlobIn(win, await fetchDocumentPdf(doc.id, "view"));
    } catch (err) {
      win?.close();
      showToast(await errorMessage(err, "Could not open the document"), "error");
    }
  }, [showToast]);

  const SEGMENTS = [
    { key: "vouchers", label: "Vouchers", Icon: Plane, count: groups.vouchers.length },
    { key: "invoices", label: "Invoices", Icon: Receipt, count: groups.invoices.length },
    { key: "confirmations", label: "Booking Docs", Icon: CheckCircle, count: groups.confirmations.length },
    { key: "payments", label: "Payment requests", Icon: CalendarClock, count: groups.payments.length },
  ];

  if (loading) {
    return (
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}><FileText size={16} /> Documents</div>
            <div className={styles.sectionSubtitle}>Loading documents…</div>
          </div>
        </div>
        <SkeletonList count={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.sectionCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 20px", background: "#fef2f2", borderRadius: 8, color: "#dc2626", fontSize: 13 }}>
          <AlertCircle size={15} /> {error}
          <button onClick={refetch} className={styles.btnSecondary} style={{ marginLeft: "auto" }}>
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <div>
          <div className={styles.sectionTitle}><FileText size={16} /> Documents</div>
          <div className={styles.sectionSubtitle}>
            {documents.length} document{documents.length !== 1 ? "s" : ""} across invoices, vouchers &amp; confirmations
          </div>
        </div>
      </div>

      {/* Segmented sub-tabs */}
      <div className={s.segmentBar} role="tablist" aria-label="Document categories">
        {SEGMENTS.map((seg) => (
          <button
            key={seg.key}
            role="tab"
            aria-selected={activeSegment === seg.key}
            className={`${s.segment} ${activeSegment === seg.key ? s.segmentActive : ""}`}
            onClick={() => setActiveSegment(seg.key)}
          >
            <seg.Icon size={15} />
            <span>{seg.label}</span>
            {seg.count > 0 && <span className={s.segmentCount}>{seg.count}</span>}
          </button>
        ))}
      </div>

      {activeSegment === "vouchers" && (
        <VouchersPanel
          docs={groups.vouchers}
          generating={generatingVouchers}
          onGenerate={handleGenerateVouchers}
          onDownload={handleDownload}
          onView={handleView}
          onSend={setSendFor}
          downloading={downloading}
          newDocIds={newDocIds}
          lastGeneratedAt={freshness.vouchers.lastGeneratedAt}
          isStale={freshness.vouchers.isStale}
        />
      )}

      {activeSegment === "invoices" && (
        <InvoicesPanel
          docs={groups.invoices}
          enabled={INVOICE_GENERATION_ENABLED}
          generating={generatingInvoices}
          onGenerate={handleGenerateInvoices}
          onDownload={handleDownload}
          onView={handleView}
          onSend={setSendFor}
          downloading={downloading}
          newDocIds={newDocIds}
          lastGeneratedAt={freshness.invoices.lastGeneratedAt}
          isStale={freshness.invoices.isStale}
        />
      )}

      {sendFor && (
        <DispatchReviewModal
          open
          documentIds={[sendFor.id]}
          initialChannel="email"
          title={`Send ${sendFor.documentNumber || "document"}`}
          onClose={() => setSendFor(null)}
          onFinished={refetchSilent}
        />
      )}

      {activeSegment === "payments" && (
        <PaymentsPanel
          bookingRef={bookingRef}
          docs={groups.payments}
          onIssue={({ api, noun }) => runGeneration({ api, noun, segment: "payments" })}
          onDownload={handleDownload}
          onView={handleView}
          onSend={setSendFor}
          downloading={downloading}
          newDocIds={newDocIds}
        />
      )}

      {activeSegment === "confirmations" && (
        <ConfirmationsPanel
          docs={groups.confirmations}
          generating={generatingConfirmation || generatingSSR || generatingInsurance}
          onGenerate={handleGenerateConfirmation}
          onDownload={handleDownload}
          onView={handleView}
          onSend={setSendFor}
          downloading={downloading}
          newDocIds={newDocIds}
          lastGeneratedAt={freshness.confirmations.lastGeneratedAt}
          isStale={freshness.confirmations.isStale}
        />
      )}
    </div>
  );
};

export default DocumentsTab;
